<script setup>
const props = defineProps({
  vacancy: {
    type: Object,
    required: true,
    default: () => {
      return {}
    },
  },
})

const user = useStrapiUser()
const tagSort = useTagSort()

const showMoreTags = ref(false)

const image = computed(() => {
  if (props.vacancy.image) return props.vacancy.image
  if (props.vacancy?.company?.logo) return props.vacancy.company.logo
  return null
})

const address = computed(() => {
  if (props.vacancy.address) {
    return props.vacancy.address
  }

  if (props.vacancy.company.address) {
    return props.vacancy.company.address
  }

  return null
})

const isProfessional = computed(() => user.value?.type === 'professional')
const isOwner = computed(
  () => user.value?.company?.id === props.vacancy?.company?.id,
)
</script>
<template>
  <section
    class="relative mb-4 cursor-pointer rounded-lg bg-white p-4 shadow"
    @click="
      navigateTo({ name: 'vacancies-slug', params: { slug: vacancy.slug } })
    "
  >
    <CompaniesActiveFlag :is-active="vacancy.status === 'published'" />
    <div class="">
      <div class="mb-4 flex">
        <div class="mb-4 md:mb-0">
          <figure
            class="relative mx-auto h-[104px] w-[104px] overflow-hidden rounded-lg border border-gray-50"
            :class="{ 'flex items-center bg-[#F8FAFC]': !image }"
          >
            <img
              v-if="image"
              :src="$upload(image, 'thumbnail')"
              alt=""
              class="absolute left-0 top-0 h-full w-full object-cover"
            />
            <img v-else class="mx-auto" src="/images/fallback.svg" />
          </figure>
        </div>
        <div class="ml-4 grow md:ml-6">
          <div class="">
            <div class="text-lg font-medium text-gray-900">
              {{ vacancy.title }}
            </div>
            <div v-if="vacancy.company" class="text-base text-gray-800">
              {{ vacancy.company.name }}

              <span v-if="address">
                <span class="disc" />{{ address.address }},
                {{ address.city }}
              </span>
            </div>
          </div>
          <div v-if="vacancy.tags" class="mt-1 hidden md:block">
            <Tag
              v-for="(tag, index) in showMoreTags
                ? tagSort.sort(vacancy.tags)
                : tagSort.sort(vacancy.tags).slice(0, 5)"
              :key="index"
              :text="tag.name"
            />
            <Button
              v-if="vacancy.tags.length > 5"
              theme="tertiary"
              class="mb-4 h-10 md:mt-4 lg:mb-0"
              @click.stop="showMoreTags = !showMoreTags"
            >
              <!-- <IconChevronDown class="h-4 w-4" /> -->
              <template #content>
                <p v-if="showMoreTags">Toon minder tags</p>
                <p v-else>Toon meer tags</p>
              </template>
              <template #icon-right>
                <IconChevronDown
                  class="ml-4 h-3 w-3 transition-transform"
                  :class="{ 'rotate-180': showMoreTags }"
                />
              </template>
            </Button>
          </div>
        </div>
      </div>

      <div
        v-if="isProfessional"
        class="flex flex-wrap items-center gap-x-20 md:ml-2"
      >
        <div v-if="vacancy.matchingscore_total !== null" class="mt-4 md:mt-0">
          <div class="text-lg font-medium text-gray-900">Match</div>
          <div class="text-base text-gray-900">
            {{ Math.round(vacancy.matchingscore_total) }}%
          </div>
        </div>

        <div
          v-if="vacancy.matchingscore_personality !== null"
          class="mt-4 md:mt-0"
        >
          <div class="text-lg font-medium text-gray-900">Persoonlijkheid</div>
          <div class="text-base text-gray-900">
            {{ Math.round(vacancy.matchingscore_personality) }}%
          </div>
        </div>

        <div
          v-if="vacancy.matchingscore_cognitive !== null"
          class="mt-4 md:mt-0"
        >
          <div class="text-lg font-medium text-gray-900">Cognitief</div>
          <div class="text-base text-gray-900">
            {{ Math.round(vacancy.matchingscore_cognitive) }}%
          </div>
        </div>

        <div
          v-if="vacancy.matchingscore_motivations !== null"
          class="mt-4 md:mt-0"
        >
          <div class="text-lg font-medium text-gray-900">Drijfveren</div>
          <div class="text-base text-gray-900">
            {{ Math.round(vacancy.matchingscore_motivations) }}%
          </div>
        </div>

        <div v-if="vacancy.matchingscore_culture !== null" class="mt-4 md:mt-0">
          <div class="text-lg font-medium text-gray-900">Cultuur</div>
          <div class="text-base text-gray-900">
            {{ Math.round(vacancy.matchingscore_culture) }}%
          </div>
        </div>
      </div>

      <div v-if="vacancy.tags" class="mt-4 block md:hidden">
        <Tag
          v-for="(tag, index) in showMoreTags
            ? tagSort.sort(vacancy.tags)
            : tagSort.sort(vacancy.tags).slice(0, 5)"
          :key="index"
          :text="tag.name"
        />
        <Button
          v-if="vacancy.tags.length > 5"
          theme="tertiary"
          class="my-2 h-10"
          @click.stop="showMoreTags = !showMoreTags"
        >
          <!-- <IconChevronDown class="h-4 w-4" /> -->
          <template #content>
            <p v-if="showMoreTags">Toon minder tags</p>
            <p v-else>Toon meer tags</p>
          </template>
          <template #icon-right>
            <IconChevronDown
              class="ml-4 h-3 w-3 transition-transform"
              :class="{ 'rotate-180': showMoreTags }"
            />
          </template>
        </Button>
      </div>

      <div class="mt-4 flex w-full flex-wrap items-center justify-end gap-2">
        <Button
          theme="transparent"
          text="Bekijken"
          size="sm"
          class="grow basis-1/3"
        />

        <Button
          v-if="isOwner"
          theme="secondary"
          text="Wijzigen"
          size="md"
          :path="`/vacancies/${vacancy?.slug}/edit`"
          class="grow basis-1/3"
        />

        <ButtonInterest
          v-if="isProfessional && vacancy?.id"
          :target-id="vacancy.id"
          target-type="vacancy"
          class="grow basis-1/3"
          @click.stop
        />
      </div>
    </div>
  </section>
</template>
<style>
span.disc:before {
  @apply mx-2 text-gray-300;
  content: '•';
}
</style>
